import { useApi } from "../../contexts/ApiContext";

// --- Payloads ---
export interface SignUpPayload {
  email: string;
  password: string;
  plan: "standard" | "pro"; // this hook supports both; your modal can pass "standard"
}

export interface VerifyPayload {
  email: string;
  code: string; // the verification code the user received by email
}

export interface ResendPayload {
  email: string;
}

// --- Response shape helper ---
export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  message?: string;
}

export const useSignUpApi = () => {
  const { post } = useApi();

  const signUp = async ({ email, password, plan }: SignUpPayload): Promise<ApiResult> => {
    const res = await post(
      "register",
      { email, password, plan },
      { api: "public", returnResponse: true }
    );

    let message: string | undefined;
    try {
      const body = await res.clone().json();
      message = (body && (body.message || body.error)) as string | undefined;
      return { ok: res.ok, status: res.status, data: body, message };
    } catch {
      return { ok: res.ok, status: res.status, message };
    }
  };

  const verify = async ({ email, code }: VerifyPayload): Promise<ApiResult> => {
    const res = await post(
      "verify",
      { email, code },
      { api: "public", returnResponse: true }
    );

    let message: string | undefined;
    try {
      const body = await res.clone().json();
      message = (body && (body.message || body.error)) as string | undefined;
      return { ok: res.ok, status: res.status, data: body, message };
    } catch {
      return { ok: res.ok, status: res.status, message };
    }
  };

  const resend = async ({ email }: ResendPayload): Promise<ApiResult> => {
    const res = await post(
      "resend-verification",
      { email },
      { api: "public", returnResponse: true }
    );

    let message: string | undefined;
    try {
      const body = await res.clone().json();
      message = (body && (body.message || body.error)) as string | undefined;
      return { ok: res.ok, status: res.status, data: body, message };
    } catch {
      return { ok: res.ok, status: res.status, message };
    }
  };

  return { signUp, verify, resend };
};